import axios from "./axios";

const normalizeIdString = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  if (typeof value === "string") {
    const t = value.trim();
    return t || undefined;
  }
  const s = String(value).trim();
  return s || undefined;
};

/** Local scope resolve to avoid circular import with utils/index. */
const resolveScopeIds = (userId?: string, workspaceId?: string) => {
  let resolvedUserId = normalizeIdString(userId);
  let resolvedWorkspaceId = normalizeIdString(workspaceId);
  if (typeof window !== "undefined") {
    if (!resolvedUserId) {
      try {
        const userRaw =
          localStorage.getItem("user") ||
          localStorage.getItem("userData") ||
          sessionStorage.getItem("user") ||
          sessionStorage.getItem("userData");
        if (userRaw) {
          const parsed = JSON.parse(userRaw);
          resolvedUserId =
            normalizeIdString(parsed?.userId) ||
            normalizeIdString(parsed?._id) ||
            resolvedUserId;
        }
      } catch {
        // ignore
      }
    }
    if (!resolvedWorkspaceId) {
      try {
        const wsRaw = localStorage.getItem("currentWorkSpace");
        if (wsRaw) {
          const parsed = JSON.parse(wsRaw);
          resolvedWorkspaceId = normalizeIdString(parsed?._id);
        }
      } catch {
        // ignore
      }
    }
  }
  return { resolvedUserId, resolvedWorkspaceId };
};

export const MAX_AI_POST_CHARS = 500;

export type AiContentStrategy = {
  _id?: string;
  ownerId?: string;
  objective?: string;
  audience?: string;
  topics?: string;
  tone?: string;
  endGoal?: string;
  productsServices?: string;
  avoidTopics?: string;
  ctaPreference?: string;
  postingFrequency?: string;
  preferredPostingTime?: string;
  timezone?: string;
  additionalInstructions?: string;
};

export type AiContentPost = {
  _id: string;
  planId: string;
  dayIndex: number;
  scheduledAt?: string | null;
  topic?: string;
  postText: string;
  cta?: string;
  status: string;
  lastError?: string;
  linkedinPostUrl?: string;
  publishedAt?: string | null;
};

export type AiContentPlan = {
  _id: string;
  status: string;
  startDate?: string;
  endDate?: string;
  strategyId?: string;
};

export type GenerationsInfo = {
  used: number;
  limit: number;
  remaining: number;
};

type ScopeIds = {
  ownerId: string;
  userId?: string;
  workspaceId?: string;
};

const withScope = (payload: ScopeIds & Record<string, unknown>) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScopeIds(
    payload.userId,
    payload.workspaceId
  );
  return {
    ...payload,
    ownerId: payload.ownerId,
    linkedinAccountId: payload.ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
  };
};

export const aiContentGetStrategy = async (ids: ScopeIds) => {
  const res = await axios.post("/ai-content/getStrategy", withScope(ids));
  return res.data;
};

export const aiContentUpsertStrategy = async (
  ids: ScopeIds,
  strategy: Partial<AiContentStrategy>
) => {
  const res = await axios.post(
    "/ai-content/upsertStrategy",
    withScope({ ...ids, ...strategy })
  );
  return res.data;
};

export const aiContentGetCurrentPlan = async (ids: ScopeIds) => {
  const res = await axios.post("/ai-content/getCurrentPlan", withScope(ids));
  return res.data;
};

export const aiContentGeneratePlan = async (ids: ScopeIds) => {
  const res = await axios.post("/ai-content/generatePlan", withScope(ids), {
    timeout: 180000,
  });
  return res.data;
};

export const aiContentApproveAll = async (ids: ScopeIds, planId: string) => {
  const res = await axios.post(
    "/ai-content/approveAllPosts",
    withScope({ ...ids, planId })
  );
  return res.data;
};

export const aiContentPausePlan = async (ids: ScopeIds, planId: string) => {
  const res = await axios.post(
    "/ai-content/pausePlan",
    withScope({ ...ids, planId })
  );
  return res.data;
};

export const aiContentUpdatePost = async (
  ids: ScopeIds,
  payload: {
    postId: string;
    postText?: string;
    topic?: string;
    cta?: string;
    scheduledAt?: string;
  }
) => {
  const res = await axios.post(
    "/ai-content/updatePost",
    withScope({ ...ids, ...payload })
  );
  return res.data;
};

export const aiContentApprovePost = async (ids: ScopeIds, postId: string) => {
  const res = await axios.post(
    "/ai-content/approvePost",
    withScope({ ...ids, postId })
  );
  return res.data;
};

export const aiContentSkipPost = async (ids: ScopeIds, postId: string) => {
  const res = await axios.post(
    "/ai-content/skipPost",
    withScope({ ...ids, postId })
  );
  return res.data;
};

export const aiContentUnskipPost = async (ids: ScopeIds, postId: string) => {
  const res = await axios.post(
    "/ai-content/unskipPost",
    withScope({ ...ids, postId })
  );
  return res.data;
};

export const aiContentRegeneratePost = async (ids: ScopeIds, postId: string) => {
  const res = await axios.post(
    "/ai-content/regeneratePost",
    withScope({ ...ids, postId }),
    { timeout: 120000 }
  );
  return res.data;
};

export const aiContentGetScheduled = async (ids: ScopeIds) => {
  const res = await axios.post("/ai-content/getScheduledPosts", withScope(ids));
  return res.data;
};

export const aiContentGetHistory = async (
  ids: ScopeIds,
  page = 1,
  limit = 20
) => {
  const res = await axios.post(
    "/ai-content/getHistory",
    withScope({ ...ids, page, limit })
  );
  return res.data;
};

export const aiContentPublishNow = async (ids: ScopeIds, postId: string) => {
  const res = await axios.post(
    "/ai-content/publishNow",
    withScope({ ...ids, postId }),
    { timeout: 90000 }
  );
  return res.data;
};

export const AI_CONTENT_ACCOUNT_KEY = "aiContentSelectedOwnerId";
export const AI_CONTENT_PLAN_VIEW_KEY = "aiContentPlanView";

/**
 * AI Poster beta allowlist (frontend hide/guard).
 * Must stay in sync with backend AI_POSTER_ALLOWED_EMAILS.
 * - comma-separated → only those emails see the menu
 * - empty / "*" / "all" → everyone
 */
export const isAiPosterAllowedForEmail = (email?: string | null) => {
  const raw = String(
    process.env.REACT_APP_AI_POSTER_ALLOWED_EMAILS || ""
  ).trim();
  if (!raw || raw === "*" || raw.toLowerCase() === "all") return true;
  const allowed = raw
    .split(/[,;\s]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  if (!allowed.length) return true;
  const normalized = String(email || "").trim().toLowerCase();
  return Boolean(normalized && allowed.includes(normalized));
};

export const getStoredAiContentOwnerId = () => {
  try {
    return localStorage.getItem(AI_CONTENT_ACCOUNT_KEY) || "";
  } catch {
    return "";
  }
};

export const setStoredAiContentOwnerId = (ownerId: string) => {
  try {
    localStorage.setItem(AI_CONTENT_ACCOUNT_KEY, ownerId);
  } catch {
    // ignore
  }
};

export type AiContentPlanView = "list" | "calendar";

export const getStoredAiContentPlanView = (): AiContentPlanView => {
  try {
    const raw = localStorage.getItem(AI_CONTENT_PLAN_VIEW_KEY);
    return raw === "calendar" ? "calendar" : "list";
  } catch {
    return "list";
  }
};

export const setStoredAiContentPlanView = (view: AiContentPlanView) => {
  try {
    localStorage.setItem(AI_CONTENT_PLAN_VIEW_KEY, view);
  } catch {
    // ignore
  }
};
